import {
  data,
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
  useFetcher,
  useLoaderData,
  useNavigation,
  type MetaFunction,
  type ShouldRevalidateFunctionArgs,
} from "react-router";
import { ThemeBody, ThemeHead, ThemeProvider } from "~/helpers/ThemeProvider";
import { getThemeSession } from "./helpers/theme.server";
import { UserProvider, useUserDetails } from "./helpers/UserContext";
import type { UserDetails } from "./models/UserDetails";
import { ToastContainer } from "react-toastify";
import { getTokenSession } from "./helpers/token.server";
import * as gtag from "./helpers/gtags.client";
import Header from "./components/Header";
import LoadingIndicator from "./components/LoadingIndicator";
import Footer from "./components/Footer";
import Navigation from "./components/Navigation";
import * as Sentry from "@sentry/browser";
import type { Route } from "./+types/root";
import stylesheet from "~/app.css?url";
import { RcProvider, useRcStatus } from "./helpers/RcContext";
import { getRcSession } from "./helpers/rc.server";
import { useEffect } from "react";
import { getCF } from "./db/db";

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  // {
  //   rel: "preconnect",
  //   href: "https://fonts.gstatic.com",
  //   crossOrigin: "anonymous",
  // },
  // {
  //   rel: "stylesheet",
  //   href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  // },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css?family=Roboto:400,400i,700,700i",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css?family=Russo+One",
  },
  {
    rel: "stylesheet",
    href: stylesheet,
  },
];
export const meta: MetaFunction = () => {
  return [{ title: "RollCall2" }];
};

export const loader = async ({ request, context }: Route.LoaderArgs) => {
  const cf = getCF(context);
  // const h = await getTokenAndUserAndCfContext(request, context);
  const themeSession = await getThemeSession(request, cf.SESSION_SECRET);
  const tokenSession = await getTokenSession(
    request,
    cf.SESSION_SECRET,
    cf.SECRET,
  );
  const userDetails: UserDetails | null = await tokenSession.getUserDetails();

  const rcSession = await getRcSession(request, context);
  const existingStatus = rcSession.getSubscriptionStatus();
  try {
    if (userDetails && !existingStatus) {
      const { rcCookie, status } = await rcSession.getSetAndCommit();
      return data(
        {
          theme: themeSession.getTheme(),
          userDetails,
          apiKey: context.cloudflare.env.REVENUE_CAT_API_KEY,
          status,
          // gaTrackingId: context.cloudflare.env.GA_TRACKING_ID,
        },
        {
          headers: [["Set-Cookie", rcCookie]],
        },
      );
    }
  } catch (error) {
    console.log(error);
  }
  return data({
    theme: themeSession.getTheme(),
    userDetails,
    apiKey: context.cloudflare.env.REVENUE_CAT_API_KEY,
    status: existingStatus,
    // gaTrackingId: context.cloudflare.env.GA_TRACKING_ID,
  });
};

export function shouldRevalidate(args: ShouldRevalidateFunctionArgs) {
  // run root loaders to remove user details at log out and log in
  if (
    args.formMethod === "POST" &&
    (args.formData?.get("subaction") === "logout" ||
      args.formData?.get("subaction") === "login")
  ) {
    return true;
  }

  return args.defaultShouldRevalidate;
}

export const action = async ({ request, context }: Route.ActionArgs) => {
  console.log("Logging out");
  const tokenSession = await getTokenSession(
    request,
    context.cloudflare.env.SESSION_SECRET,
    context.cloudflare.env.SECRET,
  );
  const cookie = await tokenSession.destroy();
  const rcSession = await getRcSession(request, context);
  const rcCookie = await rcSession.destroy();
  Sentry.setUser(null);

  return redirect("/", {
    headers: [
      ["Set-Cookie", cookie],
      ["Set-Cookie", rcCookie],
    ],
  });
};

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useLoaderData<typeof loader>();
  const navigation = useNavigation();
  const fetcher = useFetcher({ key: "doing-work" });

  // useEffect(() => {
  //   if (data?.gaTrackingId?.length) {
  //     gtag.pageview(location.pathname, data.gaTrackingId);
  //   }
  //   if (navigator.userAgent.indexOf("iPhone") > -1) {
  //     document
  //       ?.querySelector("[name=viewport]")
  //       ?.setAttribute(
  //         "content",
  //         "width=device-width, initial-scale=1, maximum-scale=1",
  //       );
  //   }
  // }, [location, data.gaTrackingId]);

  return (
    <ThemeProvider specifiedTheme={data.theme}>
      <UserProvider user={data.userDetails}>
        <RcProvider status={data.status}>
          <html lang="en" className={data.theme ?? ""}>
            <head>
              <meta charSet="utf-8" />
              <meta
                name="viewport"
                content="width=device-width, initial-scale=1"
              />
              <meta
                name="theme-color"
                content={data.theme === "dark" ? "#111827" : "#cdc7be"}
              />

              <meta property="og:title" content="RollCall2" />
              <meta property="og:description" content="Attendance made easy" />
              <meta
                property="og:image"
                content="/images/android-chrome-512x512.png.png"
              />
              <meta property="og:url" content="https://rollcall2.com" />
              <meta name="twitter:card" content="summary_large_image"></meta>
              <Meta />
              <Links />
              {/* <link rel="manifest" href="/manifest.json" /> */}
              <link
                rel="apple-touch-icon"
                sizes="180x180"
                href="/favicons/apple-touch-icon.png"
              />
              <link
                rel="icon"
                type="image/png"
                sizes="32x32"
                href="/favicons/favicon-32x32.png"
              />
              <link
                rel="icon"
                type="image/png"
                sizes="16x16"
                href="/favicons/favicon-16x16.png"
              />
              <link rel="manifest" href="/favicons/site.webmanifest" />
              {/* <link
              rel="mask-icon"
              href="/favicons/safari-pinned-tab.svg"
              color="#5bbad5"
            /> */}
              <link rel="shortcut icon" href="/favicons/favicon.ico" />
              <meta name="msapplication-TileColor" content="#2d89ef" />
              {/* <meta
              name="msapplication-config"
              content="/favicons/browserconfig.xml"
            /> */}
              <ThemeHead ssrTheme={Boolean(data?.theme)} />
            </head>
            <body
              className={`h-full w-full bg-white text-gray-600 dark:bg-gray-900 dark:tracking-wider dark:text-gray-400`}
              style={
                navigation.state === "loading"
                  ? {
                      overscrollBehavior: "none",
                      touchAction: "none",
                    }
                  : {}
              }
            >
              {(navigation.state === "loading" ||
                navigation.state === "submitting") && <LoadingIndicator />}
              {fetcher.state !== "idle" && <LoadingIndicator />}
              <Header />
              <Navigation userDetails={data?.userDetails} />
              {children}
              <Footer />
              <ThemeBody ssrTheme={Boolean(data?.theme)} />
              <ScrollRestoration />
              <Scripts />
              {/* {!data.gaTrackingId ? null : (
              <>
                <script
                  async
                  src={`https://www.googletagmanager.com/gtag/js?id=${data.gaTrackingId}`}
                />
                <script
                  async
                  id="gtag-init"
                  dangerouslySetInnerHTML={{
                    __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());

                gtag('config', '${data.gaTrackingId}', {
                  page_path: window.location.pathname,
                });
              `,
                  }}
            />
            </>
            )} */}
              <ToastContainer
                position="bottom-center"
                autoClose={3000}
                limit={5}
                closeOnClick
                theme={data.theme === "dark" ? "dark" : "light"}
              />
            </body>
          </html>
        </RcProvider>
      </UserProvider>
    </ThemeProvider>
  );
}

export default function App() {
  const loaderData = useLoaderData<typeof loader>();
  const [subscriptionStatus, setSubscriptionStatus] = useRcStatus();
  const [userDetails, setUserDetails] = useUserDetails();
  useEffect(() => {
    if (loaderData?.status) {
      setSubscriptionStatus(loaderData.status);
    }
    if (loaderData?.userDetails) {
      setUserDetails(loaderData.userDetails);
      Sentry.setUser({
        id: loaderData.userDetails.id,
        email: loaderData.userDetails.email,
      });
    }
  }, [loaderData]);

  return <Outlet />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  console.log({ errorBoundary: error });

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  if (message === "Error") {
    Sentry.captureException(error);
  }

  return (
    <main className="container mx-auto p-4 pt-16">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full overflow-x-auto p-4">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
